import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["readOnly", "required", "defaultOptions", "cache", "value", "tag_value", "type"];
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import { debounce, memoize } from 'underscore';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import * as TagsActions from 'ContentData/actions/Tags';
import * as tagsSelectors from 'ContentData/selectors/tags';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import { TAG } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { SLUG, ID, NAME, ALL } from 'ContentUtils/enums/TagDefaultValueTypes';
import hashString from 'ContentComponents/utils/hashString';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const languageI18nKey = key => `SharedI18nStrings.languageNames.${key}`;
const hashTagOptions = options => {
  return `${hashString(options.reduce((tagInfo, option) => `${tagInfo}${tagInfo && '.'}${option && option.value || null}`, ''))}`;
};
const makeMapStateToProps = () => {
  const getDefaultTagOptions = tagsSelectors.makeGetDefaultTagOptions();
  return (state, props) => {
    const tagValue = props.tag_value;
    return {
      options: tagsSelectors.getTagOptions(state, {
        tagValue
      }),
      optionsRequestStatus: tagsSelectors.getTagsCacheRequestStatus(state),
      defaultOptions: getDefaultTagOptions(state, {
        tagValue
      }),
      cache: tagsSelectors.getTagsCache(state),
      tags: tagsSelectors.getTags(state)
    };
  };
};
const mapDispatchToProps = {
  fetchTag: TagsActions.fetchTag,
  fetchTags: TagsActions.fetchTags,
  saveCache: TagsActions.saveCache,
  clearCache: TagsActions.clearCache
};
const connector = connect(makeMapStateToProps, mapDispatchToProps);
class TagSelect extends Component {
  constructor(props) {
    super(props);
    this.tagOptionsWithLanguage = memoize(options => {
      const {
        tag_value
      } = this.props;
      return options.map(option => {
        const {
          language
        } = option;
        return Object.assign({}, option, {
          help: tag_value === ALL || tag_value === ID ? this.renderTagLanguage(language) : null
        });
      });
    }, options => {
      return hashTagOptions(options);
    });
    this.onChange = this.onChange.bind(this);
    this.saveCache = this.saveCache.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchTag,
      value,
      tag_value
    } = this.props;
    fetchTag(value, tag_value).catch(err => {
      throw err;
    });
  }
  componentDidUpdate(prevProps) {
    const {
      onFieldChange = emptyFunction,
      tag_value: tagValue,
      tags,
      value
    } = this.props;
    const {
      tag_value: prevTagValue
    } = prevProps;
    if (tagValue !== prevTagValue && value && value === prevProps.value) {
      let tag = null;
      if (prevTagValue === ID) {
        tag = tags.byId[value];
      }
      if (prevTagValue === NAME) {
        tag = tags.byName[value];
      }
      if (prevTagValue === ALL) {
        tag = tags.byId[value.id];
      }
      if (prevTagValue === SLUG) {
        tag = tags.bySlug[value];
      }
      const newValue = tag && this.getTagFieldValue(tag);
      if (newValue) onFieldChange(newValue);
    }
  }
  getTag(value) {
    const {
      cache: tagsCache,
      tag_value: tagValue
    } = this.props;
    let tag = null;
    if (tagValue === ID || tagValue === ALL) {
      tag = tagsCache.byId[value];
    }
    if (tagValue === NAME) {
      tag = tagsCache.byName[value];
    }
    if (tagValue === SLUG) {
      tag = tagsCache.bySlug[value];
    }
    return tag;
  }
  getTagFieldValue(tag) {
    const {
      tag_value: tagValue
    } = this.props;
    let newValue = null;
    if (!tag) return null;
    if (tagValue !== ALL) {
      newValue = tag && tag[tagValue.toLowerCase()];
    } else {
      const {
        id,
        name,
        slug
      } = tag;
      newValue = {
        id,
        name,
        slug
      };
    }
    return newValue;
  }
  renderTagLanguage(language) {
    if (!language) {
      return null;
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        className: "m-right-1",
        name: "language",
        size: 12
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: languageI18nKey(language)
      })]
    });
  }
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      this.props.clearCache();
      offset = 0;
    }
    const query = {
      q: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    this.props.fetchTags(query).then(response => {
      const {
        total
      } = response;
      const {
        cache
      } = this.props;
      callback(null, {
        options: this.tagOptionsWithLanguage(this.props.options),
        pagination: {
          hasMore: total > Object.keys(cache.byId).length,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  onChange({
    target: {
      value
    }
  }) {
    const {
      onFieldChange = emptyFunction
    } = this.props;
    const tag = this.getTag(value);
    const newValue = this.getTagFieldValue(tag);
    onFieldChange(newValue);
  }
  saveCache(value) {
    const {
      saveCache,
      tag_value
    } = this.props;
    saveCache(value, tag_value);
  }
  render() {
    const _this$props = this.props,
      {
        readOnly,
        required,
        defaultOptions = [],
        value,
        tag_value,
        type = TAG
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    return /*#__PURE__*/_jsx(AsyncSelector, Object.assign({}, rest, {
      "data-selenium": "tag-select",
      defaultOptions: defaultOptions,
      clearable: !required,
      loadOptions: this.debouncedLoadOptions,
      onChange: this.onChange,
      readOnly: readOnly,
      saveCache: this.saveCache,
      type: type,
      value: tag_value === ALL && value ? value.id : value
    }));
  }
}
export default connector(TagSelect);